/**
 * 媒体弹窗组件：支持 SVGA、Vap（透明通道视频）、原生 MP4 的预览播放，
 * 带 loading 动画，关闭时统一停止播放并回调 onClose。
 *
 * 播放策略：
 *   - MP4 → 优先用 Vap 加载（透明通道支持），5s 内未播放则兜底为原生 <video>
 *   - SVGA → 直接使用 SVGA 库播放（Vap 不支持 SVGA 格式）
 */
import React from 'react'
import SVGA from 'svgaplayerweb'
import { Parser, Player } from 'svga'
import Vap from 'video-animation-player'

import './index.less'

// DOM 节点 id / 选择器常量
const ID_VIDEO_ELEMENT = 'oiio-video'
const SELECTOR_VIDEO = `#${ID_VIDEO_ELEMENT}`
const ID_VAP_CONTAINER = 'oiio'
const SELECTOR_VAP_CONTAINER = `#${ID_VAP_CONTAINER}`
const ID_LOADING_CANVAS = 'loadingCanvas'
const ID_SVGA_PREVIEW = 'preview-svga'
const SELECTOR_SVGA_PREVIEW = `#${ID_SVGA_PREVIEW}`

// Vap 内部 parse error 不触发 error 事件，需超时兜底
const VAP_FALLBACK_TIMEOUT = 5000

// 缺失 vap_json 时的占位配置，Vap 会在超时后走兜底逻辑
const DEFAULT_VAP_CONFIG = {
  info: {
    v: 2,
    f: 120,
    w: 400,
    h: 750,
    fps: 30,
    videoW: 608,
    videoH: 752,
    aFrame: [404, 0, 200, 375],
    rgbFrame: [0, 0, 400, 750],
    isVapx: 0,
    orien: 0,
  },
}

export default class MediaModal extends React.Component {
  constructor(props) {
    super(props)
    this.mpRef = React.createRef()
    // 兜底超时定时器，挂在实例上便于清理
    this._vapFallbackTimer = null
  }

  state = {
    vapInstance: {},
    loadingInstance: {},
    loadingVisible: true,
    mediaType: '',
    screenWidth: window.screen.width,
    screenHeight: window.screen.height,
    loadingUrl: '/img/ani_web_loading.svga',
    nativeVideoReady: false,
    vapFailed: false, // Vap 加载失败，切换到原生兜底
  }

  /** 停止 loading 动画并隐藏 loading 层 */
  hideLoading = () => {
    const { loadingInstance } = this.state
    if (loadingInstance && typeof loadingInstance.stop === 'function') {
      loadingInstance.stop()
    }
    this.setState({ loadingVisible: false })
  }

  /** 暂停并销毁 Vap 播放器实例 */
  stopVap = () => {
    const { vapInstance } = this.state
    if (vapInstance && typeof vapInstance.pause === 'function') vapInstance.pause()
    if (vapInstance && typeof vapInstance.destroy === 'function') vapInstance.destroy()
  }

  /** 暂停并清空原生 video 标签的 src */
  stopNativeVideo = () => {
    const el = document.querySelector(SELECTOR_VIDEO)
    if (el) {
      el.pause()
      el.src = ''
    }
  }

  /** 清除 Vap 兜底超时定时器 */
  clearVapFallbackTimer = () => {
    if (this._vapFallbackTimer) {
      clearTimeout(this._vapFallbackTimer)
      this._vapFallbackTimer = null
    }
  }

  /** 点击遮罩关闭：停止所有播放并重置状态，再调用 onClose */
  onCloseView = (e) => {
    e.stopPropagation()
    this.clearVapFallbackTimer()
    this.setState({ loadingVisible: true, nativeVideoReady: false, vapFailed: false })
    try {
      this.stopVap()
      this.stopNativeVideo()
      const { loadingInstance } = this.state
      if (loadingInstance && typeof loadingInstance.stop === 'function') {
        loadingInstance.stop()
      }
    } catch (error) {
      console.error('Close view error:', error)
    }
    this.props.onClose()
  }

  /** 初始化 loading 动画：加载 SVGA 并在 canvas 上播放 */
  init = async () => {
    const { loadingUrl } = this.state
    if (!loadingUrl) return
    try {
      const loadingParser = new Parser()
      const loadingSvga = await loadingParser.load(loadingUrl)
      const loadingPlayer = new Player(document.getElementById(ID_LOADING_CANVAS))
      await loadingPlayer.mount(loadingSvga)
      loadingPlayer.start()
      this.setState({ loadingInstance: loadingPlayer })
    } catch (error) {
      console.error('Loading animation error:', error)
      this.setState({ loadingVisible: false })
    }
  }

  /**
   * 使用 Vap 加载资源（MP4 和 SVGA 均从此入口尝试）。
   * 5s 内未触发 playing 事件则超时兜底，设置 vapFailed: true，
   * 由 componentDidUpdate 根据 mediaType 调用对应原生兜底方法。
   */
  initVap = () => {
    const { media } = this.props
    const container = document.querySelector(SELECTOR_VAP_CONTAINER)
    if (!container) {
      console.error('MediaModal: Vap container not found')
      this.setState({ vapFailed: true })
      return
    }

    // vap_json 支持字符串（自动解析）或对象；缺失时使用默认配置
    let config = media.vap_json || DEFAULT_VAP_CONFIG
    if (typeof config === 'string') {
      try {
        config = JSON.parse(config)
      } catch (e) {
        config = DEFAULT_VAP_CONFIG
      }
    }

    // Vap 内部 parse error 不触发 error 事件，5s 超时兜底
    this._vapFallbackTimer = setTimeout(() => {
      console.warn('[MediaModal] Vap 未在 5s 内开始播放，切换兜底')
      this.setState({ vapFailed: true })
    }, VAP_FALLBACK_TIMEOUT)

    try {
      const vapInstance = new Vap({
        container,
        src: media['show_url'],
        config,
        loop: true,
        beginPoint: 0,
        type: 1,
        precache: true,
      })
      vapInstance.on('playing', () => {
        this.clearVapFallbackTimer()
        this.hideLoading()
      })
      vapInstance.on('error', (error) => {
        console.error('Vap player error:', error)
        this.clearVapFallbackTimer()
        this.setState({ vapFailed: true })
      })
      this.setState({ vapInstance })
    } catch (error) {
      console.error('Vap initialization error:', error)
      this.clearVapFallbackTimer()
      this.setState({ vapFailed: true })
    }
  }

  /** MP4 兜底：Vap 失败时使用原生 video 播放 */
  playNativeVideoFallback = () => {
    const { media } = this.props
    this.stopVap()
    const videoElement = document.querySelector(SELECTOR_VIDEO)
    if (!videoElement) return
    videoElement.src = media['show_url']
    videoElement.load()
    const handleCanPlay = () => {
      this.setState({ nativeVideoReady: true })
      this.hideLoading()
      videoElement.play().catch((error) => {
        console.error('Video play error:', error)
        this.hideLoading()
      })
    }
    videoElement.addEventListener('canplay', handleCanPlay, { once: true })
    videoElement.addEventListener('error', () => this.hideLoading(), { once: true })
  }

  /** SVGA 直接播放：使用 SVGA 库加载并播放（不经过 Vap） */
  initSVGA = () => {
    const { media } = this.props
    try {
      const player = new SVGA.Player(SELECTOR_SVGA_PREVIEW)
      const parser = new SVGA.Parser(SELECTOR_SVGA_PREVIEW)
      parser.load(
        media['show_url'],
        (videoItem) => {
          const fixedWidth = 240
          const ratio = videoItem.videoSize.height / (videoItem.videoSize.width || 1)
          const screenHeight = Math.round(fixedWidth * ratio)
          this.setState({ screenWidth: fixedWidth, screenHeight })
          this.hideLoading()
          player.setVideoItem(videoItem)
          player.startAnimation()
        },
        (error) => {
          console.error('SVGA load error:', error)
          this.hideLoading()
        },
      )
    } catch (error) {
      console.error('SVGA initialization error:', error)
      this.hideLoading()
    }
  }

  /** media 变化且弹窗可见时：初始化 loading，按类型选择播放方式 */
  async componentDidUpdate(prevProps, prevState) {
    const { visible, media } = this.props
    const { vapFailed, mediaType } = this.state

    // media 变化 → 重置状态并初始化
    if (visible && JSON.stringify(media) !== JSON.stringify(prevProps.media)) {
      if (media && media['show_url']) {
        const url = media['show_url']
        const type = url.indexOf('.mp4') > -1 ? 'mp4' : url.indexOf('.svga') > -1 ? 'svga' : ''
        this.setState({
          mediaType: type,
          loadingVisible: true,
          nativeVideoReady: false,
          vapFailed: false,
        })
        try {
          await this.init()
        } catch (error) {
          console.error('Init loading animation error:', error)
        }
        if (type === 'mp4') {
          // MP4：优先用 Vap，延迟一帧确保容器已挂载
          setTimeout(() => this.initVap(), 0)
        } else if (type === 'svga') {
          // SVGA：直接使用 SVGA 库，不经过 Vap
          this.initSVGA()
        }
      }
    }

    // MP4 的 vapFailed 由 false → true：切换原生 video 兜底
    if (vapFailed && !prevState.vapFailed && mediaType === 'mp4') {
      this.playNativeVideoFallback()
    }
  }

  render() {
    const { media, visible } = this.props
    const { loadingVisible, screenHeight, mediaType, nativeVideoReady, vapFailed } = this.state
    const hasMediaUrl = media && media['show_url']
    const isMp4 = mediaType === 'mp4' || (hasMediaUrl && media['show_url'].indexOf('.mp4') > -1)
    const isSvga = mediaType === 'svga' || (hasMediaUrl && media['show_url'].indexOf('.svga') > -1)

    return (
      <div
        className={`preview-root ${visible ? 'active' : ''}`}
        onClick={(e) => this.onCloseView(e)}
      >
        {/* MP4：Vap 容器，失败后隐藏（保留节点避免重复挂载） */}
        {isMp4 && (
          <div
            id={ID_VAP_CONTAINER}
            className="vpn-mpfive"
            style={{ visibility: vapFailed ? 'hidden' : 'visible' }}
          />
        )}

        {/* MP4 兜底：Vap 失败后显示原生 video，canplay 后才可见 */}
        {isMp4 && vapFailed && (
          <div className="video-root">
            <video
              id={ID_VIDEO_ELEMENT}
              className="native-video"
              autoPlay
              loop
              muted
              playsInline
              webkit-playsinline="true"
              style={{
                width: '7.5rem',
                height: '100vh',
                objectFit: 'contain',
                visibility: nativeVideoReady ? 'visible' : 'hidden',
              }}
            />
          </div>
        )}

        {/* SVGA：直接显示 SVGA 预览区域，宽固定 240，高按比例自适应 */}
        {isSvga && (
          <div className="video-root">
            <div
              className="preview-svga"
              id={ID_SVGA_PREVIEW}
              style={{
                textAlign: 'center',
                width: 240,
                height: screenHeight,
              }}
            />
          </div>
        )}

        {/* 全屏 loading 动画 */}
        {loadingVisible && (
          <div className="zhi-loader-compbox">
            <div className="chatie">
              <canvas id={ID_LOADING_CANVAS}></canvas>
            </div>
          </div>
        )}
      </div>
    )
  }
}



// WEBPACK FOOTER //
// src/component/media-modal/index.js